import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Select } from 'vopay';
import useFetchAccountUserRoles from '@/hooks/useApi/useFetchAccountUserRoles';
import { useToast } from '@/providers/Toast.provider';

type SelectRoleFieldProps = {
  /** Currently selected role ID (a parent account role's RoleID), or null. */
  roleID: string | null;
  /**
   * Account token of the account the new sub-account will be created under.
   * The role list is fetched for that account, since the create endpoint
   * resolves RoleID against it. Undefined = the logged-in account.
   */
  bearer?: string;
  disabled?: boolean;
  /** Fires with the new selection, or null when cleared. */
  onChange: (roleId: string | null) => void;
};

// The new sub-account's first user can be assigned a role at creation. The
// dropdown lists all of the parent account's roles — the same list User
// Management shows for that account. The selected ID is passed to the create
// endpoint, which resolves it against the parent account and assigns it to
// the first user server-side.
export default function SelectRoleField({ roleID, bearer, disabled = false, onChange }: SelectRoleFieldProps) {
  const { t } = useTranslation('translations', { keyPrefix: 'SubAccounts.Create.Form.SelectRole' });
  const toast = useToast();
  const { fetching, roles, fetchAccountUserRoles } = useFetchAccountUserRoles();

  useEffect(() => {
    fetchAccountUserRoles(bearer).catch(() => {
      toast.neutral(t('loadRolesError', { defaultValue: 'Could not load roles' }));
    });
  }, [bearer]);

  const options = useMemo(() => roles.map((role) => ({ value: String(role.RoleID), label: role.Name })), [roles]);

  return (
    <Select
      version="2"
      id="subAccountRole"
      name="subAccountRole"
      options={options}
      value={roleID ?? ''}
      onChange={(_event, { value: selectedValue }) => onChange(selectedValue || null)}
      placeholder={t('placeholder', { defaultValue: 'Select' })}
      filterable
      showClear
      loading={fetching}
      emptyFilterMsg={t('noRolesFound', { defaultValue: 'No roles found' })}
      disabled={disabled}
    />
  );
}
